import {
  CheckCircleOutlined,
  DislikeOutlined,
  ExclamationCircleOutlined,
  LikeOutlined,
  LoadingOutlined
} from "@ant-design/icons"
import {
  Alert,
  Button,
  Flex,
  Skeleton,
  Tooltip,
  Typography,
  message
} from "antd"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { EducationArticle } from "../../../../components/Education/EducationArticle.tsx"
import { EducationAudioPlayer } from "../../../../components/Education/EducationAudioPlayer.tsx"
import { useDateFormat } from "../../../../hooks/use-date-format.ts"
import { DATE_FORMAT } from "../../../../utils/date/date.ts"
import { AskForMoreModal } from "./AskForMoreModal.tsx"
import {
  type AudienceTrackOption,
  AudienceTrackSelector,
  audienceTabId,
  audienceTabPanelId
} from "./AudienceTrackSelector.tsx"
import { CustomerPreviewModal } from "./CustomerPreviewModal.tsx"
import { CustomerPublishBar } from "./CustomerPublishBar.tsx"
import { CustomerPublishStatus } from "./CustomerPublishStatus.tsx"
import { EditionHeader } from "./EditionHeader.tsx"
import { EditionTimeline } from "./EditionTimeline.tsx"
import {
  ReportIssueModal,
  type ReportIssueSubmission
} from "./ReportIssueModal.tsx"
import { SourcesPanel } from "./SourcesPanel.tsx"
import {
  getAudio,
  markEditionViewed as markEditionViewedApi,
  retryAudio as retryAudioApi,
  saveProgress,
  sendFeedback
} from "./api/education.services.ts"
import {
  type InFlightTrackStatus,
  READABLE_EDITION_STATUSES,
  askForMoreLabel,
  buildCopySummaryText,
  customerProjectLabel,
  customerPublishState,
  defaultEditionId,
  editionLabel,
  extractPlainText,
  formatUpdatedRelative,
  simplifyInFlightStatus,
  staleFieldLabel,
  toAudioResult
} from "./education-track-helpers.ts"
import {
  type AskMoreResult,
  type LeadEducationResult
} from "./hooks/use-lead-education.ts"
import type {
  Audience,
  EditionView,
  InFlightPackageView,
  LeadEducationFlags,
  LeadEducationPermissions,
  PackageView,
  RequestMoreData,
  StartGenerationData,
  TrackView
} from "./types/education.types.ts"

const { Text } = Typography

const AUDIENCE_ORDER: Audience[] = ["sales_person", "customer"]

const trackTitleKey = (audience: Audience) =>
  audience === "customer" ? "customer" : "salesPerson"

const bannerCopyKey = (audience: Audience) =>
  audience === "customer" ? "customer" : "sales"

const HIGH_SEVERITY = "high"

const IN_FLIGHT_ICON: Record<InFlightTrackStatus, React.ReactNode> = {
  generating: <LoadingOutlined aria-hidden />,
  ready: <CheckCircleOutlined aria-hidden />,
  failed: <ExclamationCircleOutlined aria-hidden />
}

interface EducationTrackViewProps {
  canEdit: boolean
  flags: LeadEducationFlags
  permissions: LeadEducationPermissions
  packageView: PackageView
  inFlightPackage: InFlightPackageView | null
  requestedAudience: Audience | null
  onReviewInputs: () => void
  onGenerate: (body: StartGenerationData) => Promise<LeadEducationResult>
  onRetry: (editionId: string) => Promise<LeadEducationResult>
  /** Only given by the real workspace tab (a bare unit test that doesn't
   *  exercise the modal can omit it) — bound to the active track's id by
   *  this component before it reaches `AskForMoreModal`. */
  onAskMore?: (
    trackId: string,
    body: RequestMoreData,
    idempotencyKey: string
  ) => Promise<AskMoreResult>
  onInviteToMeeting?: () => void
  /** Called once a displayed edition has been successfully marked viewed, so
   *  the caller can refetch the package and pick up the updated engagement
   *  status (wired to the hook's `refresh()` by the real workspace tab) — a
   *  bare unit test that doesn't care about engagement refresh can omit it. */
  onEditionViewed?: () => void
  /** Screen 5 — customer-track only. A bare unit test that doesn't exercise
   *  publish/approve can omit either and the corresponding button simply
   *  does nothing when clicked. */
  onPublish?: (editionId: string) => Promise<LeadEducationResult>
  onApprove?: (editionId: string) => Promise<LeadEducationResult>
  /** "Edit lead details" — navigates the lead workspace to its Details tab
   *  (`key: "additional"`), the same helper `EducationEmptyState`'s own
   *  "Add" links already use. */
  onGoToTab?: (key: string) => void
  /** The customer preview's "Prepared for" (the customer's first name) and
   *  "Shared by" (the sales person's full name), as the public page shows
   *  them. */
  preparedFor?: string | null
  sharedBy?: string | null
}

/**
 * Screens 2 and 4 — the populated Project Education tab. Owns which audience
 * track and which edition of it are on screen; `LeadEducationTab` owns
 * nothing about "what's showing", only the data fetch/poll and the actions
 * that mutate it.
 */
export function EducationTrackView({
  canEdit,
  flags,
  permissions,
  packageView,
  inFlightPackage,
  requestedAudience,
  onReviewInputs,
  onGenerate,
  onRetry,
  onAskMore,
  onInviteToMeeting,
  onEditionViewed,
  onPublish,
  onApprove,
  onGoToTab,
  preparedFor,
  sharedBy
}: EducationTrackViewProps) {
  const { t } = useTranslation()
  const { userFormat } = useDateFormat()
  const dateFormat = userFormat || DATE_FORMAT
  const [selectedAudience, setSelectedAudience] = useState<Audience>(
    requestedAudience &&
      packageView.tracks.some((track) => track.audience === requestedAudience)
      ? requestedAudience
      : "sales_person"
  )
  const [editionSelection, setEditionSelection] = useState<
    Record<string, string>
  >({})
  const [sourcesOpen, setSourcesOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [reportIssueEditionId, setReportIssueEditionId] = useState<
    string | null
  >(null)
  const [reportIssueSubmitting, setReportIssueSubmitting] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const [retryingId, setRetryingId] = useState<string | null>(null)
  const [askMoreOpen, setAskMoreOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [publishingId, setPublishingId] = useState<string | null>(null)
  const [approvingId, setApprovingId] = useState<string | null>(null)

  const availableTracks = useMemo(
    () =>
      AUDIENCE_ORDER.filter((audience) =>
        packageView.tracks.some((tr) => tr.audience === audience)
      ),
    [packageView.tracks]
  )

  // The customer card always shows its own publish state, regardless of
  // which track is currently active — it's chrome on the selector itself,
  // not part of the "what's showing below" state.
  const customerTrack = packageView.tracks.find(
    (track) => track.audience === "customer"
  )
  const customerDefaultEdition = customerTrack?.editions.find(
    (edition) => edition.id === defaultEditionId(customerTrack.editions)
  )
  const selectorOptions: AudienceTrackOption[] = availableTracks.map(
    (audience) => ({
      audience,
      publishState:
        audience === "customer" && customerDefaultEdition
          ? customerPublishState(customerDefaultEdition)
          : undefined
    })
  )

  // A regenerate can drop the selected audience's track; fall back to one
  // that exists rather than hiding the whole view, selector included.
  const activeAudience: Audience = availableTracks.includes(selectedAudience)
    ? selectedAudience
    : (availableTracks[0] ?? selectedAudience)

  const activeTrack: TrackView | undefined = packageView.tracks.find(
    (track) => track.audience === activeAudience
  )

  const selectedEditionId = activeTrack
    ? editionSelection[activeTrack.id]
    : undefined
  const activeEditionId = activeTrack
    ? selectedEditionId &&
      activeTrack.editions.some((edition) => edition.id === selectedEditionId)
      ? selectedEditionId
      : defaultEditionId(activeTrack.editions)
    : null

  const activeEdition: EditionView | undefined = activeTrack?.editions.find(
    (edition) => edition.id === activeEditionId
  )

  const selectEdition = (trackId: string, editionId: string) =>
    setEditionSelection((prev) => ({ ...prev, [trackId]: editionId }))

  const canRegenerate = canEdit && permissions.can_generate
  // Same right as Regenerate (both create new content) plus the track's own
  // depth limit — live testing found the button reachable with neither
  // `canEdit` nor `can_generate`, which Task 5 had left ungated.
  const canAskMore = canRegenerate && (activeTrack?.remaining ?? 0) > 0

  const handleRegenerate = async () => {
    setRegenerating(true)
    const result = await onGenerate({})
    setRegenerating(false)
    if (!result.ok) message.error(result.error.message)
  }

  const handleRetry = async (editionId: string) => {
    setRetryingId(editionId)
    const result = await onRetry(editionId)
    setRetryingId(null)
    if (!result.ok) message.error(result.error.message)
  }

  const handlePublish = async (editionId: string) => {
    if (!onPublish) return
    setPublishingId(editionId)
    const result = await onPublish(editionId)
    setPublishingId(null)
    if (!result.ok) message.error(result.error.message)
  }

  const handleApprove = async (editionId: string) => {
    if (!onApprove) return
    setApprovingId(editionId)
    const result = await onApprove(editionId)
    setApprovingId(null)
    if (!result.ok) message.error(result.error.message)
  }

  const handleAskMoreSubmit = (
    body: RequestMoreData,
    idempotencyKey: string
  ): Promise<AskMoreResult> => {
    if (!activeTrack || !onAskMore) {
      return Promise.resolve({
        ok: false as const,
        error: {
          code: "network_error",
          message: t("projectEducation.error.network"),
          request_id: "",
          retryable: true,
          field_errors: {}
        }
      })
    }
    return onAskMore(activeTrack.id, body, idempotencyKey)
  }

  const handleAskMoreSuccess = (edition: EditionView | null) => {
    setAskMoreOpen(false)
    if (!activeTrack) return
    if (edition) {
      // Pin the selector to the edition we just created — `refresh()` (run
      // by `askMore` itself before this fires) has already put it in
      // `activeTrack.editions`, so this shows its pending state immediately
      // and keeps following it in place as later polls flip it to ready.
      // `defaultEditionId` alone can't do this: it prefers a *readable*
      // edition over the newest one, so a still-pending "More" would lose
      // to the existing Base.
      selectEdition(activeTrack.id, edition.id)
    } else {
      setEditionSelection((prev) => {
        const next = { ...prev }
        delete next[activeTrack.id]
        return next
      })
    }
  }

  const handleCopySummary = useCallback(
    async (edition: EditionView, audience: Audience) => {
      const title = t(
        `projectEducation.tracks.${trackTitleKey(audience)}.title`
      )
      const text = buildCopySummaryText(title, edition, t)
      try {
        await navigator.clipboard.writeText(text)
      } catch {
        void message.error(t("projectEducation.trackView.copyFailed"))
        return
      }
      void message.success(t("projectEducation.trackView.copied"))
    },
    [t]
  )

  const handleFeedback = async (
    edition: EditionView,
    rating: "up" | "down"
  ) => {
    const response = await sendFeedback(edition.id, { kind: "rating", rating })
    if (response.ok) {
      void message.success(t("projectEducation.trackView.feedbackThanks"))
    } else {
      void message.error(
        response.data?.message ?? t("projectEducation.error.network")
      )
    }
  }

  const handleReportIssueSubmit = async ({
    sentence,
    reason
  }: ReportIssueSubmission) => {
    if (!reportIssueEditionId) return
    setReportIssueSubmitting(true)
    const response = await sendFeedback(reportIssueEditionId, {
      kind: "issue",
      sentence,
      reason
    })
    setReportIssueSubmitting(false)
    if (response.ok) {
      void message.success(t("projectEducation.reportIssueModal.submitted"))
      setReportIssueEditionId(null)
    } else {
      void message.error(
        response.data?.message ?? t("projectEducation.error.network")
      )
    }
  }

  const fetchAudioForActive = useCallback(async () => {
    if (!activeEdition) return { status: "failed" as const }
    const response = await getAudio(activeEdition.id)
    return toAudioResult(response.ok ? response.data?.details.data : null)
  }, [activeEdition])

  const retryAudioForActive = useCallback(async () => {
    if (!activeEdition) return
    await retryAudioApi(activeEdition.id)
  }, [activeEdition])

  const handleAudioProgress = useCallback(
    (positionMs: number, completed: boolean) => {
      if (!activeEdition) return
      void saveProgress(activeEdition.id, {
        position_ms: Math.round(positionMs),
        completed
      })
    },
    [activeEdition]
  )

  // Records that an internal user read this edition, once per edition per
  // mount, and only once it has content: the backend keeps the first view
  // date forever, so a pending edition must not claim it. Fire-and-forget; a
  // successful mark asks the caller to refresh the History status.
  const viewedEditionIdsRef = useRef<Set<string>>(new Set())
  const readableEditionId =
    activeEdition && READABLE_EDITION_STATUSES.includes(activeEdition.status)
      ? activeEdition.id
      : undefined
  useEffect(() => {
    const editionId = readableEditionId
    if (!editionId || viewedEditionIdsRef.current.has(editionId)) return
    viewedEditionIdsRef.current.add(editionId)
    void (async () => {
      try {
        const response = await markEditionViewedApi(editionId)
        if (response?.ok) onEditionViewed?.()
      } catch {
        // Fire-and-forget — see the comment above.
      }
    })()
  }, [readableEditionId, onEditionViewed])

  if (!activeTrack) return null

  const updatedAtIso = activeEdition?.created_at ?? packageView.created_at
  const highSeverityFlags =
    activeAudience === "sales_person" && activeEdition
      ? activeEdition.safety_flags.filter(
          (flag) => flag.severity === HIGH_SEVERITY
        )
      : []
  const isStale = packageView.stale_fields.length > 0
  const isReadable =
    !!activeEdition && READABLE_EDITION_STATUSES.includes(activeEdition.status)
  // Screen 5's status/preview row and its own footer both need a real
  // edition to describe — nothing to publish or preview yet while the
  // edition is still pending/failed/blocked.
  const showCustomerPublishUI = activeAudience === "customer" && isReadable

  return (
    <div data-testid={`education-track-view-${activeAudience}`}>
      <Flex
        justify={"space-between"}
        align={"flex-start"}
        wrap
        gap={"small"}
        className={"mb-3"}
      >
        <Text type={"secondary"}>
          {formatUpdatedRelative(updatedAtIso, new Date(), t)}
        </Text>
        <Flex gap={"small"} align={"flex-start"}>
          <Flex vertical gap={2}>
            <Tooltip
              title={
                canRegenerate
                  ? undefined
                  : t("projectEducation.trackView.regenerateDisabled")
              }
            >
              <span>
                <Button
                  disabled={!canRegenerate}
                  loading={regenerating}
                  onClick={() => void handleRegenerate()}
                >
                  {t("projectEducation.trackView.regenerate")}
                </Button>
              </span>
            </Tooltip>
            {isStale && (
              <Text
                type={canRegenerate ? "secondary" : "danger"}
                className={"text-xs"}
              >
                {canRegenerate
                  ? t("projectEducation.trackView.staleRegenerateHint")
                  : t("projectEducation.trackView.staleRegenerateHintNoAccess")}
              </Text>
            )}
          </Flex>
          {onInviteToMeeting && (
            <Button onClick={onInviteToMeeting}>
              {t("projectEducation.trackView.inviteToMeeting")}
            </Button>
          )}
        </Flex>
      </Flex>

      <AudienceTrackSelector
        options={selectorOptions}
        activeAudience={activeAudience}
        onSelect={setSelectedAudience}
      />

      <div
        role={"tabpanel"}
        id={audienceTabPanelId(activeAudience)}
        aria-labelledby={audienceTabId(activeAudience)}
      >
        <Alert
          className={"mb-3"}
          type={activeAudience === "customer" ? "success" : "info"}
          showIcon
          data-testid={"audience-context-banner"}
          message={t(
            `projectEducation.trackSelector.${bannerCopyKey(activeAudience)}.banner`
          )}
        />

        {inFlightPackage && (
          <Alert
            className={"mb-3"}
            type={"info"}
            showIcon
            message={t("projectEducation.trackView.regeneratingBanner")}
            description={
              <Flex gap={"middle"} wrap className={"mt-1"}>
                {inFlightPackage.tracks.map((track) => {
                  const status = simplifyInFlightStatus(track)
                  return (
                    <Flex
                      key={track.id}
                      align={"center"}
                      gap={6}
                      data-testid={`in-flight-track-${track.audience}`}
                    >
                      {IN_FLIGHT_ICON[status]}
                      <Text>
                        {t(
                          `projectEducation.tracks.${trackTitleKey(track.audience)}.title`
                        )}
                        :{" "}
                        {t(
                          `projectEducation.trackView.inFlightStatus.${status}`
                        )}
                      </Text>
                    </Flex>
                  )
                })}
              </Flex>
            }
          />
        )}

        {packageView.stale_fields.length > 0 && (
          <Alert
            className={"mb-3"}
            type={"warning"}
            showIcon
            message={
              <Flex align={"center"} gap={8} wrap>
                <span>
                  {t("projectEducation.trackView.staleBanner", {
                    fields: packageView.stale_fields
                      .map((field) => staleFieldLabel(field, t))
                      .join(", ")
                  })}
                </span>
                <Button type={"link"} size={"small"} onClick={onReviewInputs}>
                  {t("projectEducation.trackView.staleReview")}
                </Button>
              </Flex>
            }
          />
        )}

        {activeEdition && (
          <>
            <EditionHeader
              editions={activeTrack.editions}
              selectedEdition={activeEdition}
              dateFormat={dateFormat}
              onSelectEdition={(id) => selectEdition(activeTrack.id, id)}
              onOpenHistory={() => setHistoryOpen(true)}
            >
              {(showCustomerPublishUI ||
                (flags.audio && activeEdition.status !== "pending")) && (
                <Flex align={"center"} gap={"middle"} wrap>
                  {showCustomerPublishUI && (
                    <CustomerPublishStatus
                      edition={activeEdition}
                      dateFormat={dateFormat}
                      onPreview={() => setPreviewOpen(true)}
                    />
                  )}
                  {flags.audio && activeEdition.status !== "pending" && (
                    <EducationAudioPlayer
                      key={activeEdition.id}
                      fetchAudio={fetchAudioForActive}
                      retryAudio={retryAudioForActive}
                      initialPositionMs={activeEdition.progress_ms}
                      onProgress={handleAudioProgress}
                      transcript={extractPlainText(activeEdition.html)}
                      compactOnMobile
                      title={`${t(
                        `projectEducation.tracks.${trackTitleKey(activeAudience)}.title`
                      )} — ${editionLabel(activeEdition, t)}`}
                      labels={{
                        play: t("projectEducation.audioPlayer.play"),
                        pause: t("projectEducation.audioPlayer.pause"),
                        back15: t("projectEducation.audioPlayer.back15"),
                        restart: t("projectEducation.audioPlayer.restart"),
                        speed: t("projectEducation.audioPlayer.speed"),
                        preparing: t("projectEducation.audioPlayer.preparing"),
                        audioReady: t(
                          "projectEducation.audioPlayer.audioReady"
                        ),
                        autoplayBlocked: t(
                          "projectEducation.audioPlayer.autoplayBlocked"
                        ),
                        failed: t("projectEducation.audioPlayer.failed"),
                        retry: t("projectEducation.audioPlayer.retry"),
                        transcriptShow: t(
                          "projectEducation.audioPlayer.transcriptShow"
                        ),
                        transcriptHide: t(
                          "projectEducation.audioPlayer.transcriptHide"
                        ),
                        back15Short: t(
                          "projectEducation.audioPlayer.back15Short"
                        ),
                        seek: t("projectEducation.audioPlayer.seek"),
                        elapsedSeconds: (count) =>
                          t("projectEducation.audioPlayer.elapsedSeconds", {
                            count
                          })
                      }}
                    />
                  )}
                </Flex>
              )}
            </EditionHeader>

            {highSeverityFlags.length > 0 && (
              <Alert
                className={"my-3"}
                type={"warning"}
                showIcon
                message={t("projectEducation.trackView.checkBeforeUsing")}
                description={
                  <ul className={"list-disc pl-5"}>
                    {highSeverityFlags.map((flag, index) => (
                      <li key={`${flag.code}-${index}`}>
                        {flag.detail ?? flag.code}
                      </li>
                    ))}
                  </ul>
                }
              />
            )}

            <div className={"mt-3"}>
              {activeEdition.status === "pending" && (
                <div role={"status"} data-testid={"edition-pending"}>
                  <Skeleton active paragraph={{ rows: 4 }} />
                  <Text type={"secondary"}>
                    {t("projectEducation.trackView.pendingHint")}
                  </Text>
                </div>
              )}

              {(activeEdition.status === "failed" ||
                activeEdition.status === "blocked") && (
                <Flex vertical gap={8} data-testid={"edition-failed"}>
                  <Flex align={"center"} gap={8}>
                    <ExclamationCircleOutlined aria-hidden />
                    <Text type={"danger"}>
                      {activeEdition.status === "blocked"
                        ? t("projectEducation.trackView.blocked", {
                            code: activeEdition.error_code ?? ""
                          })
                        : t("projectEducation.trackView.failed")}
                    </Text>
                  </Flex>
                  {activeEdition.retryable && (
                    <Button
                      loading={retryingId === activeEdition.id}
                      onClick={() => void handleRetry(activeEdition.id)}
                      data-testid={"retry-edition"}
                    >
                      {t("projectEducation.trackView.retry")}
                    </Button>
                  )}
                </Flex>
              )}

              {activeEdition.status === "superseded" && (
                <Alert
                  className={"mb-3"}
                  type={"info"}
                  showIcon
                  icon={<CheckCircleOutlined />}
                  message={t("projectEducation.trackView.superseded")}
                />
              )}

              {isReadable && (
                <EducationArticle
                  html={activeEdition.html}
                  citations={activeEdition.citations}
                  onOpenSources={() => setSourcesOpen(true)}
                  labels={{
                    viewSources: (count) =>
                      t("projectEducation.article.viewSources", { count }),
                    sourcesLabel: t("projectEducation.article.sourcesLabel")
                  }}
                />
              )}

              {showCustomerPublishUI && (
                <Text
                  type={"secondary"}
                  className={"text-xs mt-2 block"}
                  data-testid={"customer-editing-helper-text"}
                >
                  {t("projectEducation.customerPublishBar.editingHelperText")}
                </Text>
              )}
            </div>

            {showCustomerPublishUI && (
              <CustomerPublishBar
                edition={activeEdition}
                canPublish={canEdit && permissions.can_publish}
                canApprove={permissions.can_approve}
                canRegenerate={canRegenerate}
                publishing={publishingId === activeEdition.id}
                approving={approvingId === activeEdition.id}
                regenerating={regenerating}
                onOpenSources={() => setSourcesOpen(true)}
                onEditLeadDetails={() => onGoToTab?.("additional")}
                onRegenerate={() => void handleRegenerate()}
                onPublish={() => void handlePublish(activeEdition.id)}
                onApprove={() => void handleApprove(activeEdition.id)}
              />
            )}

            <Flex
              justify={"space-between"}
              align={"center"}
              wrap
              gap={"small"}
              className={"mt-4"}
            >
              <Tooltip
                title={
                  canAskMore || activeTrack.remaining <= 0
                    ? undefined
                    : t("projectEducation.askMore.disabledHint")
                }
              >
                <span>
                  <Button
                    disabled={!canAskMore}
                    onClick={() => setAskMoreOpen(true)}
                  >
                    {askForMoreLabel(activeTrack.remaining, t)}
                  </Button>
                </span>
              </Tooltip>

              <Flex gap={"small"} wrap>
                {isReadable && (
                  <>
                    <Button
                      onClick={() =>
                        void handleCopySummary(activeEdition, activeAudience)
                      }
                    >
                      {t("projectEducation.trackView.copySummary")}
                    </Button>
                    <Button
                      aria-label={t("projectEducation.trackView.feedbackUp")}
                      icon={<LikeOutlined />}
                      onClick={() => void handleFeedback(activeEdition, "up")}
                    />
                    <Button
                      aria-label={t("projectEducation.trackView.feedbackDown")}
                      icon={<DislikeOutlined />}
                      onClick={() => void handleFeedback(activeEdition, "down")}
                    />
                  </>
                )}
                <Button
                  onClick={() => setReportIssueEditionId(activeEdition.id)}
                >
                  {t("projectEducation.trackView.reportIssue")}
                </Button>
              </Flex>
            </Flex>
          </>
        )}
      </div>

      <SourcesPanel
        open={sourcesOpen}
        citations={activeEdition?.citations ?? []}
        onClose={() => setSourcesOpen(false)}
      />

      {activeEdition && (
        <EditionTimeline
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          editions={activeTrack.editions}
          maxEditions={activeTrack.max_editions}
          activeEditionId={activeEdition.id}
          dateFormat={dateFormat}
          onOpenEdition={(id) => {
            selectEdition(activeTrack.id, id)
            setHistoryOpen(false)
          }}
          onPlayEdition={(id) => {
            selectEdition(activeTrack.id, id)
            setHistoryOpen(false)
          }}
          onCopyEdition={(id) => {
            const edition = activeTrack.editions.find(
              (candidate) => candidate.id === id
            )
            if (edition) void handleCopySummary(edition, activeAudience)
          }}
          onReportIssue={(id) => {
            setReportIssueEditionId(id)
            setHistoryOpen(false)
          }}
        />
      )}

      <ReportIssueModal
        open={reportIssueEditionId != null}
        submitting={reportIssueSubmitting}
        onCancel={() => setReportIssueEditionId(null)}
        onSubmit={(body) => void handleReportIssueSubmit(body)}
      />

      <AskForMoreModal
        open={askMoreOpen}
        audience={activeAudience}
        onCancel={() => setAskMoreOpen(false)}
        onSubmit={handleAskMoreSubmit}
        onSuccess={handleAskMoreSuccess}
      />

      <CustomerPreviewModal
        open={previewOpen}
        edition={activeAudience === "customer" ? (activeEdition ?? null) : null}
        projectLabel={customerProjectLabel(packageView.project_type?.name, t)}
        preparedFor={preparedFor}
        sharedBy={sharedBy}
        dateFormat={dateFormat}
        onClose={() => setPreviewOpen(false)}
      />
    </div>
  )
}
